/**
 * Admin listing-suspension contract — what an operator posts to
 * `POST /api/admin/listings/[id]/suspend` to suspend (or lift the suspension
 * of) a single listing for a Terms of Service violation.
 *
 * Suspension is encoded as `is_unlisted = true` + `unlisted_reason =
 * 'admin_tos_violation'` on the `listings` row, so every public filter that
 * already gates on `is_unlisted` excludes suspended listings for free. The
 * reason column is what separates an operator suspension (seller cannot lift
 * it — DB trigger `listings_guard_admin_unlist`) from a seller self-hide
 * (`unlisted_reason IS NULL`).
 *
 * NOT mobile-consumed — admin-gated, so the schema can tighten freely.
 *
 * @see app/api/admin/listings/[id]/suspend/route.ts — POST handler
 * @see lib/admin/listing-suspension.ts — pure transition decision
 * @see lib/admin/suspend-listing.ts — client adapter (builder)
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Legal values of `listings.unlisted_reason` (kept in sync with the DB CHECK
 * in migration 20260804223105). NULL on the row = seller self-hide.
 */
export const UNLISTED_REASONS = [
  "admin_tos_violation",
  "paypal_seller_restricted_autopause",
] as const;

export type UnlistedReason = (typeof UNLISTED_REASONS)[number];

/** The sentinel an operator suspension writes into `unlisted_reason`. */
export const ADMIN_SUSPENSION_REASON =
  "admin_tos_violation" as const satisfies UnlistedReason;

export interface AdminListingSuspensionRequest {
  action: "suspend" | "unsuspend";
}

export const AdminListingSuspensionRequestSchema =
  pinned<AdminListingSuspensionRequest>()(
    z.object({
      action: z.enum(["suspend", "unsuspend"]),
    })
  );

export interface AdminListingSuspensionResponse {
  success: true;
  /** Suspension state after the call (idempotent — repeats are no-ops). */
  suspended: boolean;
  /** false when the listing was already in the requested state. */
  changed: boolean;
  /** Whether the ToS-violation email to the seller was sent on this call. */
  emailSent: boolean;
}

export const AdminListingSuspensionResponseSchema =
  pinned<AdminListingSuspensionResponse>()(
    z.object({
      success: z.literal(true),
      suspended: z.boolean(),
      changed: z.boolean(),
      emailSent: z.boolean(),
    })
  );

/**
 * One row of the moderation monitor (`GET /api/admin/listings/suspended`).
 * `imageUrl` arrives resolved (absolute); `suspendedAt` is ISO-8601.
 */
export interface AdminSuspendedListingRow {
  id: string;
  sellerId: string;
  sellerUsername: string | null;
  title: string;
  price: number | null;
  currency: string | null;
  imageUrl: string | null;
  suspendedAt: string | null;
  suspendedByUsername: string | null;
}

export const AdminSuspendedListingRowSchema = pinned<AdminSuspendedListingRow>()(
  z.object({
    id: z.string(),
    sellerId: z.string(),
    sellerUsername: z.string().nullable(),
    title: z.string(),
    price: z.number().nullable(),
    currency: z.string().nullable(),
    imageUrl: z.string().nullable(),
    suspendedAt: z.string().nullable(),
    suspendedByUsername: z.string().nullable(),
  })
);

export interface AdminSuspendedListingsResponse {
  success: true;
  listings: AdminSuspendedListingRow[];
  /** Total suspended count (SQL-side, not page length — max_rows discipline). */
  total: number;
}

export const AdminSuspendedListingsResponseSchema =
  pinned<AdminSuspendedListingsResponse>()(
    z.object({
      success: z.literal(true),
      listings: z.array(AdminSuspendedListingRowSchema),
      total: z.number(),
    })
  );
